import { apiRequest, downloadFile, postFormData } from "@/lib/api-client-v2";
import type {
  UdgspzEntityCreatePayload,
  UdgspzMaterialCreatePayload,
  UdgspzMaterialItem,
  UdgspzEntityDetail,
  UdgspzEntityListResponse,
  UdgspzEntityMeta,
  UdgspzEntityPatchPayload,
  UdgspzMapGeometryCreatePayload,
  UdgspzMapGeometryItem,
  UdgspzMapContourItem,
  UdgspzMapSketchPayload,
  UdgspzEntityMapStylePayload,
  UdgspzTypeMapStyle,
} from "@/types/udgspz-entity";
import type { UdgspzEntityCardResponse, UdgspzEntityCardUpdateRequest } from "@/types/udgspz-entity-card";
import type {
  UdgspzEntityHierarchyResponse,
  UdgspzObjectRegionLinkListResponse,
  UdgspzObjectRegionLinkRefreshResponse,
  UdgspzResolveRegionRequest,
  UdgspzResolveRegionResponse,
} from "@/types/udgspz-resolve";
import type {
  GoRegionLinkItem,
  GoRegionLinkListResponse,
  UdgspzCountryLinkCreateRequest,
  UdgspzCountryLinkRefreshResponse,
  UdgspzInfraLinkListResponse,
  UdgspzInfraLinkRefreshResponse,
} from "@/types/module-spatial-bridges";
import type { Geometry } from "@/types/api-v2";

class UdgspzEntitiesService {
  private readonly baseUrl = "/udgspz-entities";

  async list(params?: {
    skip?: number;
    limit?: number;
    q?: string;
    include_deleted?: boolean;
    deleted_only?: boolean;
    valid_at_from?: string;
    valid_at_to?: string;
  }): Promise<UdgspzEntityListResponse> {
    return apiRequest<UdgspzEntityListResponse>(this.baseUrl, {
      params: {
        skip: params?.skip ?? 0,
        limit: params?.limit ?? 50,
        ...(params?.q ? { q: params.q } : {}),
        ...(params?.include_deleted ? { include_deleted: true } : {}),
        ...(params?.deleted_only ? { deleted_only: true } : {}),
        ...(params?.valid_at_from ? { valid_at_from: params.valid_at_from } : {}),
        ...(params?.valid_at_to ? { valid_at_to: params.valid_at_to } : {}),
      },
    });
  }

  async getHeatmapPoints(params?: {
    valid_at_from?: string;
    valid_at_to?: string;
    limit?: number;
  }): Promise<GeoJSON.FeatureCollection> {
    return apiRequest<GeoJSON.FeatureCollection>(`${this.baseUrl}/heatmap-points`, {
      params: {
        ...(params?.valid_at_from ? { valid_at_from: params.valid_at_from } : {}),
        ...(params?.valid_at_to ? { valid_at_to: params.valid_at_to } : {}),
        ...(params?.limit != null ? { limit: params.limit } : {}),
      },
    });
  }

  async softDelete(eudgId: number): Promise<void> {
    await apiRequest<void>(`${this.baseUrl}/${eudgId}`, { method: "DELETE" });
  }

  async restore(eudgId: number): Promise<UdgspzEntityDetail> {
    return apiRequest<UdgspzEntityDetail>(`${this.baseUrl}/${eudgId}/restore`, {
      method: "POST",
    });
  }

  async getById(eudgId: number): Promise<UdgspzEntityDetail> {
    return apiRequest<UdgspzEntityDetail>(`${this.baseUrl}/${eudgId}`);
  }

  async create(payload: UdgspzEntityCreatePayload): Promise<UdgspzEntityDetail> {
    return apiRequest<UdgspzEntityDetail>(this.baseUrl, {
      method: "POST",
      data: payload,
    });
  }

  async getMeta(): Promise<UdgspzEntityMeta> {
    return apiRequest<UdgspzEntityMeta>(`${this.baseUrl}/meta`);
  }

  async listMapContours(): Promise<UdgspzMapContourItem[]> {
    const data = await apiRequest<{ items: UdgspzMapContourItem[] }>(`${this.baseUrl}/map-contours`);
    return data.items;
  }

  async updateTypeMapStyle(
    klsId: number,
    payload: { fill_color: string; line_color: string; pattern_code: string },
  ): Promise<UdgspzTypeMapStyle> {
    return apiRequest<UdgspzTypeMapStyle>(`${this.baseUrl}/type-map-styles/${klsId}`, {
      method: "PUT",
      data: payload,
    });
  }

  async updateEntityMapStyle(eudgId: number, payload: UdgspzEntityMapStylePayload): Promise<UdgspzEntityDetail> {
    return apiRequest<UdgspzEntityDetail>(`${this.baseUrl}/${eudgId}/map-style`, {
      method: "PUT",
      data: payload,
    });
  }

  async updateGeometryMapStyle(
    eudgId: number,
    eudgMapGeomId: number,
    payload: UdgspzEntityMapStylePayload,
  ): Promise<UdgspzMapGeometryItem> {
    return apiRequest<UdgspzMapGeometryItem>(
      `${this.baseUrl}/${eudgId}/map-geometries/${eudgMapGeomId}/map-style`,
      {
        method: "PUT",
        data: payload,
      },
    );
  }

  async getHierarchy(params?: {
    root_id?: number;
    kls_id_eudg_type?: number;
    include_deleted?: boolean;
    deleted_only?: boolean;
  }): Promise<UdgspzEntityHierarchyResponse> {
    return apiRequest<UdgspzEntityHierarchyResponse>(`${this.baseUrl}/hierarchy`, {
      params: {
        ...(params?.root_id != null ? { root_id: params.root_id } : {}),
        ...(params?.kls_id_eudg_type != null ? { kls_id_eudg_type: params.kls_id_eudg_type } : {}),
        ...(params?.include_deleted ? { include_deleted: true } : {}),
        ...(params?.deleted_only ? { deleted_only: true } : {}),
      },
    });
  }

  async resolveRegion(payload: UdgspzResolveRegionRequest): Promise<UdgspzResolveRegionResponse> {
    return apiRequest<UdgspzResolveRegionResponse>(`${this.baseUrl}/resolve-region`, {
      method: "POST",
      data: payload,
    });
  }

  async listObjectRegionLinks(objectId: number): Promise<UdgspzObjectRegionLinkListResponse> {
    return apiRequest<UdgspzObjectRegionLinkListResponse>(`${this.baseUrl}/object-region-links`, {
      params: { object_id: objectId },
    });
  }

  async refreshObjectRegionLinks(objectId: number): Promise<UdgspzObjectRegionLinkRefreshResponse> {
    return apiRequest<UdgspzObjectRegionLinkRefreshResponse>(`${this.baseUrl}/object-region-links/refresh`, {
      method: "POST",
      data: { object_id: objectId },
    });
  }

  async listInfraLinks(eudgId: number): Promise<UdgspzInfraLinkListResponse> {
    return apiRequest<UdgspzInfraLinkListResponse>(`${this.baseUrl}/${eudgId}/infra-links`);
  }

  async refreshInfraLinks(eudgId: number): Promise<UdgspzInfraLinkRefreshResponse> {
    return apiRequest<UdgspzInfraLinkRefreshResponse>(`${this.baseUrl}/${eudgId}/infra-links/refresh`, {
      method: "POST",
    });
  }

  async listCountryLinks(eudgId: number): Promise<GoRegionLinkListResponse> {
    return apiRequest<GoRegionLinkListResponse>(`${this.baseUrl}/${eudgId}/country-links`);
  }

  async createCountryLink(eudgId: number, payload: UdgspzCountryLinkCreateRequest): Promise<GoRegionLinkItem> {
    return apiRequest<GoRegionLinkItem>(`${this.baseUrl}/${eudgId}/country-links`, {
      method: "POST",
      data: payload,
    });
  }

  async refreshCountryLinks(eudgId: number): Promise<UdgspzCountryLinkRefreshResponse> {
    return apiRequest<UdgspzCountryLinkRefreshResponse>(`${this.baseUrl}/${eudgId}/country-links/refresh`, {
      method: "POST",
    });
  }

  async deleteCountryLink(eudgId: number, linkId: number): Promise<void> {
    await apiRequest<void>(`${this.baseUrl}/${eudgId}/country-links/${linkId}`, {
      method: "DELETE",
    });
  }

  async patch(eudgId: number, payload: UdgspzEntityPatchPayload): Promise<UdgspzEntityDetail> {
    return apiRequest<UdgspzEntityDetail>(`${this.baseUrl}/${eudgId}`, {
      method: "PATCH",
      data: payload,
    });
  }

  async getCard(eudgId: number): Promise<UdgspzEntityCardResponse> {
    return apiRequest<UdgspzEntityCardResponse>(`${this.baseUrl}/${eudgId}/card`);
  }

  async putCard(eudgId: number, payload: UdgspzEntityCardUpdateRequest): Promise<UdgspzEntityCardResponse> {
    return apiRequest<UdgspzEntityCardResponse>(`${this.baseUrl}/${eudgId}/card`, {
      method: "PUT",
      data: payload,
    });
  }

  async listMapGeometries(eudgId: number): Promise<UdgspzMapGeometryItem[]> {
    return apiRequest<UdgspzMapGeometryItem[]>(`${this.baseUrl}/${eudgId}/map-geometries`);
  }

  async createMapGeometry(
    eudgId: number,
    payload: UdgspzMapGeometryCreatePayload,
  ): Promise<UdgspzMapGeometryItem> {
    return apiRequest<UdgspzMapGeometryItem>(`${this.baseUrl}/${eudgId}/map-geometries`, {
      method: "POST",
      data: payload,
    });
  }

  async createMapFromGeography(eudgId: number, geographyId: number): Promise<UdgspzMapGeometryItem> {
    return apiRequest<UdgspzMapGeometryItem>(`${this.baseUrl}/${eudgId}/map-geometries/from-geography`, {
      method: "POST",
      data: { geography_id: geographyId },
    });
  }

  async createMapSketch(eudgId: number, payload: UdgspzMapSketchPayload): Promise<UdgspzMapGeometryItem> {
    return apiRequest<UdgspzMapGeometryItem>(`${this.baseUrl}/${eudgId}/map-geometries/sketch`, {
      method: "POST",
      data: payload,
    });
  }

  async deleteMapGeometry(eudgId: number, eudgMapGeomId: number): Promise<void> {
    await apiRequest<void>(`${this.baseUrl}/${eudgId}/map-geometries/${eudgMapGeomId}`, {
      method: "DELETE",
    });
  }

  async listMaterials(eudgId: number): Promise<UdgspzMaterialItem[]> {
    return apiRequest<UdgspzMaterialItem[]>(`${this.baseUrl}/${eudgId}/materials`);
  }

  async createMaterial(eudgId: number, payload: UdgspzMaterialCreatePayload): Promise<UdgspzMaterialItem> {
    return apiRequest<UdgspzMaterialItem>(`${this.baseUrl}/${eudgId}/materials`, {
      method: "POST",
      data: payload,
    });
  }

  async uploadMaterial(eudgId: number, file: File): Promise<UdgspzMaterialItem> {
    const formData = new FormData();
    formData.append("file", file);
    return postFormData<UdgspzMaterialItem>(`${this.baseUrl}/${eudgId}/materials/upload`, formData);
  }

  async downloadMaterialBlob(eudgId: number, udgFileId: number): Promise<Blob> {
    return downloadFile(`${this.baseUrl}/${eudgId}/materials/${udgFileId}/download`, {
      stream: true,
    });
  }

  async deleteMaterial(eudgId: number, udgFileId: number): Promise<void> {
    await apiRequest<void>(`${this.baseUrl}/${eudgId}/materials/${udgFileId}`, {
      method: "DELETE",
    });
  }

  async getGeometryById(geometryId: number): Promise<Geometry> {
    return apiRequest<Geometry>(`/geometries/${geometryId}`);
  }
}

export const udgspzEntitiesApi = new UdgspzEntitiesService();

